import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, FormEvent } from "react";
import { queryClient } from "@/lib/queryClient";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import {
  Edit,
  Trash2,
  PlusCircle,
  RefreshCw,
  Check,
  X,
  Coins,
  Gem,
} from "lucide-react";
import { CoinPackageDialog } from "@/components/ui/coin-package-dialog";
import { apiRequest } from "@/lib/api";

// Schema for coin package form data - Corrected structure
const coinPackageFormSchema = z.object({
  name: z
    .string()
    .min(1, "Package name is required")
    .max(50, "Package name too long"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(200, "Description too long"),
  amount: z.number().int().positive("Amount must be a positive number"),
  coins: z.number().int().positive("Coins must be a positive number"),
  coinType: z.enum(["regular", "premium"]).default("regular"),
  isActive: z.boolean(),
});

// Define a more specific type for the package data - Corrected structure
type CoinPackage = {
  id: number;
  name: string;
  description: string;
  amount: number;
  coins: number;
  coinType: "regular" | "premium";
  isActive: boolean;
};

type CoinPackageFormData = z.infer<typeof coinPackageFormSchema>;

// Export the component correctly
export function CoinPackagesTab() {
  const { toast } = useToast();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<CoinPackage | null>(null);

  const {
    data: packages,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["/api/currency/coin-packages"],
    queryFn: () => apiRequest<CoinPackage[]>("/api/currency/coin-packages"),
  });

  const addPackageMutation = useMutation({
    mutationFn: async (data: CoinPackageFormData) => {
      return apiRequest<CoinPackage>("/api/currency/coin-packages", { // Expect single package on creation
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/coin-packages"] });
      toast({
        title: "Package added successfully",
        description: "The new coin package has been added to the system.",
      });
      setIsAddDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding package",
        description:
          error.message ?? "Failed to add the package. Please try again.", // Use nullish coalescing
        variant: "destructive",
      });
    },
  });

  const updatePackageMutation = useMutation({
    mutationFn: async (data: { id: number; package: CoinPackageFormData }) => {
      return apiRequest<CoinPackage>(`/api/currency/coin-packages/${data.id}`, { // Expect single package on update
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.package),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/coin-packages"] });
      toast({
        title: "Package updated successfully",
        description: "The coin package has been updated.",
      });
      setIsEditDialogOpen(false);
      setSelectedPackage(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating package",
        description:
          error.message ?? "Failed to update the package. Please try again.", // Use nullish coalescing
        variant: "destructive",
      });
    },
  });

  const deletePackageMutation = useMutation({
    mutationFn: async (id: number) => {
      // Assuming DELETE returns success/failure, not the deleted object
      return apiRequest<void>(`/api/currency/coin-packages/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/coin-packages"] });
      toast({
        title: "Package deleted successfully",
        description: "The coin package has been removed from the system.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting package",
        description:
          error.message ?? "Failed to delete the package. Please try again.", // Use nullish coalescing
        variant: "destructive",
      });
    },
  });

  const handleAddSubmit = (data: CoinPackageFormData) => {
    try {
      const validatedData = coinPackageFormSchema.parse(data);
      addPackageMutation.mutate(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleEditSubmit = (data: CoinPackageFormData) => {
    if (!selectedPackage) return;
    try {
      const validatedData = coinPackageFormSchema.parse(data);
      updatePackageMutation.mutate({ id: selectedPackage.id, package: validatedData });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    deletePackageMutation.mutate(id);
  };

  const openEditDialog = (pkg: CoinPackage) => {
    setSelectedPackage(pkg);
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading coin packages</h3>
        <p className="text-muted-foreground">{error.message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["/api/currency/coin-packages"] })
          }
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add New Package
        </Button>
      </div>

      <Table>
        <TableCaption>A list of all coin packages in the system.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Amount (GHS)</TableHead>
            <TableHead>Coins</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!packages || packages.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center">
                No coin packages found. Add your first package to get started.
              </TableCell>
            </TableRow>
          ) : (
            packages.map((pkg) => (
              <TableRow key={pkg.id}>
                <TableCell className="font-medium">{pkg.id}</TableCell>
                <TableCell>{pkg.name}</TableCell>
                <TableCell>{pkg.description}</TableCell>
                <TableCell>{pkg.amount}</TableCell>
                <TableCell>
                  <span className="flex items-center">
                    {pkg.coinType === "premium" ? (
                      <Gem className="h-4 w-4 mr-1 text-purple-500" />
                    ) : (
                      <Coins className="h-4 w-4 mr-1 text-amber-500" />
                    )}
                    {pkg.coins}
                  </span>
                </TableCell>
                <TableCell>
                  {pkg.coinType === "premium" ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <Gem className="h-3 w-3 mr-1" />
                      Premium
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <Coins className="h-3 w-3 mr-1" />
                      Regular
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {pkg.isActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Check className="w-3 h-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <X className="w-3 h-3 mr-1" />
                      Inactive
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(pkg)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove the coin package "{pkg.name}" from the system.
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(pkg.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <CoinPackageDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleAddSubmit}
        mode="add"
        isLoading={addPackageMutation.isPending}
      />

      {selectedPackage && (
        <CoinPackageDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleEditSubmit}
          initialData={selectedPackage}
          mode="edit"
          isLoading={updatePackageMutation.isPending}
        />
      )}
    </div>
  );
}
